import { FinanceResponseError, requireFinancePage, requireFinanceAccount } from '../../lib/financeResponse';
import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { adminTransactionExportRow, adminTransactionExportColumns } from '../../lib/adminTransactionExport';
import { adminAPI, SettlementHistory, SettlementMerchant, SettlementRequest, SettlementPreview, SettlementPlatform, PlatformSettlementAmountSource } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Pagination } from '../ui/pagination';

const money = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString('ru-RU', { maximumFractionDigits: 6 }) : '—';
type AccountKind = 'merchant' | 'platform';
const storageKey = (id: string, kind: AccountKind) => kind === 'platform'
    ? `admin-platform-settlement-period-pending:${id}` : `admin-settlement-period-pending:${id}`;
const utcDate = (value: string) => new Date(/[Zz]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)
    .toLocaleString('ru-RU', { timeZone: 'UTC' }) + ' UTC+0';
const errorMessage = (error: any) => error instanceof FinanceResponseError ? error.message : typeof error?.response?.data?.detail === 'string'
    ? error.response.data.detail : 'Не удалось загрузить данные. Повторите попытку.';

type FinanceAccount = SettlementMerchant & { platform?: SettlementPlatform };
type FinanceHistory = Omit<SettlementHistory, 'merchant'> & { merchant: FinanceAccount };
const platformAccount = (platform: SettlementPlatform): FinanceAccount => ({
    id: platform.id, username: platform.name, available_balance: platform.available_balance ?? 0,
    actual_settlement_total: platform.actual_settlement_total,
    settlement_balance: platform.settlement_balance, settlement_sequence: platform.settlement_sequence, platform,
});
const sourceName = (source?: PlatformSettlementAmountSource | null) => source === 'external_net_usdt'
    ? 'Наш расчёт после комиссии' : source === 'external_reported_usdt' ? 'Сумма от провайдера' : 'Не настроен';
const accountBalance = (account: FinanceAccount) => account.platform && !account.platform.balance_ready ? '—' : money(account.available_balance);
const ComparisonHeaders = () => <>
    <th className="p-3">Наша сумма, USDT</th>
    <th className="p-3">Сумма провайдера, USDT</th>
    <th className="p-3">Разница, USDT</th>
</>;
const ComparisonCells = ({ platform }: { platform?: SettlementPlatform }) => {
    const comparison = platform?.comparison;
    const amount = (value?: number | null) => value == null ? '—' : money(value);
    return <>
        <td className="p-3">{amount(comparison?.our_total_usdt)}
            {!!comparison?.our_missing_count && <p className="text-xs text-amber-700 dark:text-amber-300">Нет суммы у {comparison.our_missing_count} сделок</p>}
        </td>
        <td className="p-3">{amount(comparison?.provider_total_usdt)}
            {!!comparison?.provider_missing_count && <p className="text-xs text-amber-700 dark:text-amber-300">Нет суммы у {comparison.provider_missing_count} сделок</p>}
        </td>
        <td className="p-3">{amount(comparison?.difference_usdt)}</td>
    </>;
};
const comparisonDescription = 'За всю историю завершённых реальных сделок: входящие минус выплаты по каждому источнику. Разница = наша сумма − сумма провайдера. Списание сеттлов учтено отдельно в доступном балансе.';
const financeAPI = {
    merchant: {
        list: adminAPI.getSettlementMerchants,
        history: adminAPI.getSettlements,
        preview: adminAPI.previewSettlement,
        export: adminAPI.exportSettlement,
        create: adminAPI.createSettlement,
    },
    platform: {
        list: async (page: number) => {
            const result = await adminAPI.getSettlementPlatforms(page);
            requireFinancePage(result);
            result.items.forEach(requireFinanceAccount);
            return { ...result, items: result.items.map(platformAccount) };
        },
        history: async (id: string, page: number) => {
            const result = await adminAPI.getPlatformSettlements(id, page);
            requireFinanceAccount(result?.platform);
            return { ...result, merchant: platformAccount(result.platform) };
        },
        preview: adminAPI.previewPlatformSettlement,
        export: adminAPI.exportPlatformSettlement,
        create: async (id: string, data: SettlementRequest) => {
            const result = await adminAPI.createPlatformSettlement(id, data);
            requireFinanceAccount(result?.platform);
            return { ...result, merchant: platformAccount(result.platform) };
        },
    },
};

export const SettlementPanel: React.FC<{
    kind?: AccountKind; canManage: boolean; onCompleted?: (merchant: SettlementMerchant) => void;
}> = ({ kind = 'merchant', canManage, onCompleted }) => {
    const service = financeAPI[kind];
    const isPlatform = kind === 'platform';
    const [merchants, setMerchants] = useState<FinanceAccount[]>([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(0);
    const [selected, setSelected] = useState<FinanceAccount | null>(null);
    const [history, setHistory] = useState<FinanceHistory | null>(null);
    const [historyPage, setHistoryPage] = useState(1);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [preview, setPreview] = useState<SettlementPreview | null>(null);
    const [comment, setComment] = useState('');
    const [actualAmount, setActualAmount] = useState('');
    const [pending, setPending] = useState<SettlementRequest | null>(null);
    const [busy, setBusy] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const loadFailed = !!loadError;
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [refresh, setRefresh] = useState(0);
    const submitting = useRef(false);
    const selectedId = selected?.id;
    const actualUnits = (value: string): number | null => {
        const normalized = value.trim().replace(',', '.');
        if (!/^\d+(?:\.\d{1,6})?$/.test(normalized)) return null;
        const units = Math.round(Number(normalized) * 1e6);
        return Number.isSafeInteger(units) && Number(normalized) < 1e9 ? units : null;
    };
    const amountUnits = actualUnits(actualAmount);
    const actualValid = amountUnits !== null && amountUnits > 0;

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError('');
        const load = async () => {
            try {
                const list = await service.list(page);
                const detail = selectedId ? await service.history(selectedId, historyPage) : null;
                if (!active) return;
                requireFinancePage(list);
                list.items.forEach(requireFinanceAccount);
                if (selectedId) {
                    requireFinancePage(detail);
                    requireFinanceAccount(detail?.merchant);
                }
                setLoadError('');
                setMerchants(list.items);
                setPages(list.pages);
                setHistory(detail);
                if (detail) setSelected(detail.merchant);
            } catch (err) {
                if (active) { setLoadError(errorMessage(err)); }
            } finally {
                if (active) setLoading(false);
            }
        };
        void load();
        return () => { active = false; };
    }, [page, selectedId, historyPage, refresh, service]);

    const select = (merchant: FinanceAccount) => {
        setSelected(merchant);
        setHistory(null);
        setHistoryPage(1);
        setNotice('');
        setError('');
        setPreview(null);
        let request: SettlementRequest | null = null;
        try {
            const saved = JSON.parse(localStorage.getItem(storageKey(merchant.id, kind)) || 'null');
            if (saved?.date_from && saved?.date_to && saved?.fingerprint && saved?.request_id) request = saved;
        } catch { /* No valid saved request. */ }
        setPending(request);
        setDateFrom(request ? new Date(request.date_from).toISOString().slice(0, 19) : '');
        setDateTo(request ? new Date(request.date_to).toISOString().slice(0, 19) : '');
        setComment(request?.comment || '');
        setActualAmount(request?.actual_amount || '');
    };

    const calculate = async () => {
        if (!selected || pending || busy || loadFailed || selected.platform?.balance_ready === false) return;
        if (!dateFrom || !dateTo || dateFrom >= dateTo) {
            setError('Укажите начало и конец периода. Начало должно быть раньше конца.');
            return;
        }
        setBusy(true);
        setError('');
        setNotice('');
        setPreview(null);
        try {
            setPreview(await service.preview(selected.id, {
                date_from: new Date(`${dateFrom}Z`).toISOString(),
                date_to: new Date(`${dateTo}Z`).toISOString(),
            }));
        } catch (err) {
            setError(errorMessage(err));
        } finally { setBusy(false); }
    };

    const exportTransactions = async () => {
        if (!selected || !preview || !canManage || pending || busy || submitting.current || loadFailed) return;
        submitting.current = true;
        setBusy(true);
        setExporting(true);
        setError('');
        setNotice('');
        try {
            const result = await service.export(selected.id, {
                date_from: preview.date_from, date_to: preview.date_to, fingerprint: preview.fingerprint,
            });
            if (result.fingerprint !== preview.fingerprint || result.total !== preview.transaction_count
                || !Array.isArray(result.items) || result.items.length !== preview.transaction_count
                || result.items.some(item => !item.transaction_id)
                || new Set(result.items.map(item => item.transaction_id)).size !== result.total) {
                setPreview(null);
                throw new Error('Состав выгрузки не совпадает с расчётом. Обновите расчёт.');
            }
            const sheet = XLSX.utils.json_to_sheet(result.items.map(item => ({
                ...adminTransactionExportRow(item),
                'Вклад в сеттл, USDT': item.settlement_contribution_usdt ?? '',
            })));
            sheet['!cols'] = [...adminTransactionExportColumns, { wch: 24 }];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, sheet, 'Transactions');
            XLSX.writeFile(workbook, `settlement_${kind}_${selected.id}_${preview.date_from.slice(0, 10)}_${preview.date_to.slice(0, 10)}.xlsx`);
        } catch (err: any) {
            if (err?.response?.status === 409) setPreview(null);
            setError(err?.response ? errorMessage(err) : err instanceof Error ? err.message : 'Не удалось выгрузить сделки.');
        } finally {
            submitting.current = false;
            setBusy(false);
            setExporting(false);
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selected || !canManage || busy || submitting.current || (!pending && (loadFailed || !preview?.can_settle))) return;
        if (!pending && !actualValid) {
            setError('Введите положительную фактическую сумму сеттла (до 6 знаков после запятой, меньше 1 млрд USDT).');
            return;
        }
        const request: SettlementRequest = pending || {
            actual_amount: actualAmount.trim().replace(',', '.'),
            date_from: preview!.date_from, date_to: preview!.date_to,
            fingerprint: preview!.fingerprint, comment: comment.trim(),
            request_id: crypto.randomUUID(), expected_sequence: preview!.expected_sequence,
        };
        // Persist before sending: a reload must retry the exact operation.
        try { localStorage.setItem(storageKey(selected.id, kind), JSON.stringify(request)); }
        catch { setError('Не удалось сохранить запрос в браузере. Проведение не начато.'); return; }
        setPending(request);
        submitting.current = true;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const result = await service.create(selected.id, request);
            localStorage.removeItem(storageKey(selected.id, kind));
            setPending(null);
            setPreview(null);
            setComment('');
            setActualAmount('');
            setSelected(result.merchant);
            if (!isPlatform) onCompleted?.(result.merchant);
            setHistoryPage(1);
            setNotice(`Сеттл проведён. Расчётная сумма: ${money(result.settlement.amount)} USDT. Фактическая сумма: ${money(result.settlement.actual_amount!)} USDT.`);
            setRefresh(value => value + 1);
        } catch (err: any) {
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500) {
                localStorage.removeItem(storageKey(selected.id, kind));
                setPending(null);
                setPreview(null);
                setError(errorMessage(err));
                setRefresh(value => value + 1);
            } else {
                setError('Результат запроса не подтверждён. Повторите запрос позже: повторного списания не будет.');
            }
        } finally {
            submitting.current = false;
            setBusy(false);
        }
    };

    return <Card>
        <CardHeader><CardTitle>{isPlatform ? 'Платформы' : 'Мерчанты'}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">{isPlatform ? 'Сеттлы платформ каскада: депозиты минус выплаты. Все суммы в USDT.' : 'Сеттлы с мерчантами: учёт выплат, выполненных вручную. Все суммы в USDT.'}</p>
            {(error || loadError) && <p role="alert" className="text-red-600 dark:text-red-300">{error || loadError}</p>}
            {notice && <p role="status" className="text-emerald-700 dark:text-emerald-300">{notice}</p>}
            <Button variant="outline" disabled={busy || loading} onClick={() => { setPreview(null); setRefresh(value => value + 1); }}>Обновить</Button>
            {loading && <p role="status">Загрузка…</p>}
            {!selected ? <>
                {isPlatform && <p className="text-sm text-slate-600 dark:text-slate-300">{comparisonDescription}</p>}
                <div className="overflow-x-auto"><table className="w-full text-left text-sm">
                    <thead><tr><th className="p-3">{isPlatform ? 'Платформа' : 'Мерчант'}</th>{isPlatform && <ComparisonHeaders />}<th>Доступно, USDT</th><th>Сумма сеттлов, USDT</th><th className="p-3">Фактическая сумма сеттлов, USDT</th></tr></thead>
                    <tbody>{merchants.map(merchant => <tr key={merchant.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-900">
                        <td className="p-3"><button className="text-blue-600 dark:text-blue-300 underline" onClick={() => select(merchant)}>{merchant.username || merchant.id}</button></td>
                        {isPlatform && <ComparisonCells platform={merchant.platform} />}
                        <td>{accountBalance(merchant)}</td><td>{money(merchant.settlement_balance)}</td><td className="p-3">{money(merchant.actual_settlement_total)}</td>
                    </tr>)}</tbody>
                </table></div>
                {!loading && !loadFailed && !merchants.length && <p>{isPlatform ? 'Платформы не найдены.' : 'Мерчанты не найдены.'}</p>}
                <Pagination currentPage={page} totalPages={pages} onPageChange={setPage} />
            </> : <>
                <Button variant="outline" disabled={busy} onClick={() => { setSelected(null); setPreview(null); setNotice(''); }}>{isPlatform ? 'Все платформы' : 'Все мерчанты'}</Button>
                <h3 className="text-lg font-semibold">{selected.username}</h3>
                <p>Доступно: <strong>{accountBalance(selected)} USDT</strong> · Проведено сеттлов на {money(selected.settlement_balance)} USDT</p>
                <p>Фактическая сумма проведённых сеттлов: {money(selected.actual_settlement_total)} USDT</p>
                {selected.platform && <section aria-label="Сравнение сумм платформы" className="space-y-2">
                    <p className="text-sm text-slate-600 dark:text-slate-300">{comparisonDescription}</p>
                    <div className="overflow-x-auto"><table className="w-full text-left text-sm">
                        <thead><tr><ComparisonHeaders /></tr></thead>
                        <tbody><tr><ComparisonCells platform={selected.platform} /></tr></tbody>
                    </table></div>
                </section>}
                {selected.platform && <section className="rounded border p-4 space-y-3 max-w-2xl" aria-label="Источник сумм платформы">
                    <p>Источник сумм: <strong>{sourceName(selected.platform.amount_source)}</strong></p>
                    {selected.platform.balance_reason && <p role="alert" className="text-amber-700 dark:text-amber-300">{selected.platform.balance_reason}</p>}
                    {selected.platform.balance_ready && <p>За всю историю: депозиты {money(selected.platform.deposit_balance ?? 0)} USDT · выплаты {money(selected.platform.withdraw_balance ?? 0)} USDT</p>}
                </section>}
                {!isPlatform && selected.advance && <p role="status" className="text-amber-700 dark:text-amber-300">Сеттлы недоступны после выдачи аванса.</p>}
                {canManage && !selected.advance && <form onSubmit={submit} className="space-y-3 max-w-2xl">
                    <Label htmlFor="settlement-from">Начало периода, UTC+0 (включительно)</Label>
                    <Input id="settlement-from" type="datetime-local" step="1" value={dateFrom}
                        onChange={event => { setDateFrom(event.target.value); setPreview(null); }} disabled={busy || !!pending} required />
                    <Label htmlFor="settlement-to">Конец периода, UTC+0 (не включается)</Label>
                    <Input id="settlement-to" type="datetime-local" step="1" value={dateTo}
                        onChange={event => { setDateTo(event.target.value); setPreview(null); }} disabled={busy || !!pending} required />
                    <p className="text-sm text-slate-500 dark:text-slate-400">По дате успешного завершения. Уже учтённые в сеттлах сделки исключаются.</p>
                    <Button type="button" variant="outline" onClick={calculate} disabled={busy || loading || loadFailed || !!pending || selected.platform?.balance_ready === false}>Рассчитать</Button>
                    {preview && <div className="rounded border p-4 space-y-2" aria-label="Расчёт сеттла">
                        <p className="break-all">Первый ID по созданию заявки: <strong>{preview.first_transaction_id || '—'}</strong></p>
                        <p className="break-all">Последний ID по созданию заявки: <strong>{preview.last_transaction_id || '—'}</strong></p>
                        <p>Транзакций: {preview.transaction_count}</p>
                        <Button type="button" variant="outline" onClick={exportTransactions}
                            disabled={busy || loading || loadFailed || !!pending || !preview.transaction_count}>
                            {exporting ? 'Выгрузка…' : 'Выгрузить сделки в Excel'}
                        </Button>
                        <p>Входящие после комиссии: {money(preview.deposit_total_usdt)} USDT ({preview.deposit_count})</p>
                        <p>Выплаты с комиссией: {money(preview.withdraw_total_usdt)} USDT ({preview.withdraw_count})</p>
                        <p>Сумма сеттла: <strong>{money(preview.amount)} USDT</strong></p>
                        <p>Баланс после списания: {money(preview.balance_after)} USDT</p>
                        {preview.reason && <p role="alert" className="text-amber-700 dark:text-amber-300 break-words">{preview.reason}</p>}
                    </div>}
                    {(preview?.can_settle || pending) && <>
                        <Label htmlFor={`${kind}-actual-amount`}>Фактическая сумма сеттла, USDT</Label>
                        <Input id={`${kind}-actual-amount`} inputMode="decimal" value={actualAmount}
                            onChange={event => setActualAmount(event.target.value)} disabled={busy || !!pending} required />
                        {!pending && actualAmount && !actualValid && <p role="alert" className="text-amber-700 dark:text-amber-300">Укажите сумму больше 0 и меньше 1 млрд USDT (до 6 знаков после запятой).</p>}
                        <Label htmlFor="settlement-comment">Комментарий (необязательно)</Label>
                        <Input id="settlement-comment" value={comment} maxLength={1000} onChange={event => setComment(event.target.value)} disabled={busy || !!pending} />
                        {pending && !busy && <p className="text-sm text-amber-700 dark:text-amber-300">Есть запрос с неподтверждённым результатом. Повторите его для получения результата.</p>}
                        <Button type="submit" disabled={busy || loading || (!pending && (loadFailed || !actualValid))}>
                            {busy ? 'Обработка…' : pending ? 'Повторить запрос' : 'Провести сеттл'}
                        </Button>
                    </>}
                </form>}
                <h3 className="font-semibold">История сеттлов</h3>
                <div className="overflow-x-auto"><table className="w-full text-left text-sm">
                    <thead><tr>{['Дата', 'Период UTC+0', 'Первый / последний ID по созданию заявки', 'Сделок', 'Входящие / выплаты, USDT', 'Сумма, USDT', 'До / после', 'Фактическая сумма, USDT', 'Администратор', 'Комментарий'].map(label => <th key={label} className="p-2">{label}</th>)}</tr></thead>
                    <tbody>{history?.items.map(record => <tr key={record._id} className="border-t">
                        <td className="p-2 whitespace-nowrap">{utcDate(record.created_at)}</td>
                        <td className="p-2 whitespace-nowrap">{utcDate(record.date_from)}<br />{utcDate(record.date_to)}</td>
                        <td className="p-2 font-mono">{record.first_transaction_id}<br />{record.last_transaction_id}</td>
                        <td className="p-2">{record.transaction_count} ({record.deposit_count} / {record.withdraw_count})</td>
                        <td className="p-2">{money(record.deposit_total_usdt)} / {money(record.withdraw_total_usdt)}</td>
                        <td className="p-2">{money(record.amount)}{isPlatform && <p className="text-xs text-slate-500 dark:text-slate-400">{sourceName(record.amount_source)}</p>}</td>
                        <td className="p-2">{money(record.balance_before)} / {money(record.balance_after)}</td>
                        <td className="p-2">{record.actual_amount == null ? '—' : money(record.actual_amount)}</td>
                        <td className="p-2">{record.admin_username}</td><td className="p-2 break-words max-w-sm">{record.comment || '—'}</td>
                    </tr>)}</tbody>
                </table></div>
                {!loading && !loadFailed && history && !history.total && <p>Сеттлы ещё не проводились.</p>}
                <Pagination currentPage={historyPage} totalPages={history?.pages || 0} onPageChange={setHistoryPage} />
            </>}
        </CardContent>
    </Card>;
};
